/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-05 12:23:03                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2024-12-24 10:32:44                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * QuotationTable: Quotations table section
*/

/* Imports */
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { Chip, Tooltip, Typography } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { parseISO, format } from "date-fns";
import { CheckIcon, ClockIcon, DocumentTextIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";

/* Components */
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import CustomerDisplay from "../../../components/data/customer/CustomerDisplay";
import ContactDisplay from "../../../components/data/contact/ContactDisplay";
import TechnicianDisplay from "../../../components/data/technician/TechnicianDisplay";
import { getQuotationStatusStyle } from "../../../components/data/quotation-status/quotationStatusStyles";

export default function QuotationsTable({
  quotations,
  selectedHead,
  orderDesc,
  onHeadClick
}: {
  quotations: EasyTech.Quotation[],
  selectedHead?: string,
  orderDesc?: boolean,
  onHeadClick?: (head: string, orderDesc: boolean) => void 
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1440);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 1440);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const TABLE_HEAD = [
    {
      label: t("common:ref:label"),
      orderKey: "ref",
      width: "10%"
    },{
      label: t("common:date:label"),
      orderKey: "date",
      width: "10%"
    },{
      label: t("common:customer"),
      orderKey: "customer.name",
      width: "30%"
    },{
      label: t("common:contact"),
      orderKey: "contact.lastname",
      width: "20%"
    },{
      label: t("common:technician"),
      orderKey: "technician.user.lastName",
      width: "20%"
    },{
      label: t("common:status"),
      orderKey: "status",
      width: isSmallScreen ? "6%" : "10%",
      headClassName: isSmallScreen ? "text-center" : ""
    }
  ];

  const getQuotationStatusBadge = (status?: string) => {
    const normalizedStatus = status?.toUpperCase() || "";
    const label = status ? t(`quotation:status.${status.toLowerCase()}`) : "-";
    const style = getQuotationStatusStyle(status);

    if (isSmallScreen) {
      const getIcon = () => {
        switch (normalizedStatus) {
          case "CREATED":
            return <DocumentTextIcon className="h-4 w-4" />;
          case "SENT":
            return <PaperAirplaneIcon className="h-4 w-4" />;
          case "ACCEPTED":
          case "DONE":
            return <CheckIcon className="h-4 w-4" />;
          default:
            return <ClockIcon className="h-4 w-4" />;
        }
      };

      return (
        <Tooltip content={label}>
          <span className="et-cell-icon-badge" style={{ backgroundColor: style.soft, color: style.text }}>
            {getIcon()}
          </span>
        </Tooltip>
      );
    }

    return (
      <Chip
        size="sm"
        variant="ghost"
        value={label}
        className="w-fit h-fit rounded-full py-1 capitalize text-xs"
        style={{ backgroundColor: style.soft, color: style.text }}
      />
    );
  };

  return(
    <Table  tableHead={TABLE_HEAD} emptyTable={(!quotations || quotations.length === 0) ? t("quotation:noData") : ""} 
    selectedHead={selectedHead} orderDesc={orderDesc}
    onHeadClick={onHeadClick}
    enableRowLinks={true}>
      {quotations.map((quotation, index) => {
        const isLast = index === quotations.length - 1;
        const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";
        const quotationUrl = `/quotations/${quotation.id}`;
        const contactLabel = quotation.contact
          ? `${quotation.contact.firstName} ${quotation.contact.lastName}`.trim()
          : "-";
        const technicianLabel = quotation.technician
          ? `${quotation.technician.firstName} ${quotation.technician.lastName}`.trim()
          : "-";

        return (
          <tr key={`${quotation.id}_${index}`} className="hover:bg-blue-gray-50/50 cursor-pointer" onClick={() => navigate(quotationUrl)} data-href={quotationUrl}>
            <td className={classes}>
              <OverflowTooltip content={quotation.ref} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {quotation.ref}
                </Typography>
              </OverflowTooltip>
            </td>

            <td className={classes}>
              {(() => {
                const dateString = (() => {
                  if (!quotation.date) return "-";
                  try {
                    const date = typeof quotation.date === "string" ? parseISO(quotation.date) : new Date(quotation.date);
                    return isNaN(date.getTime()) ? "-" : format(date, "dd/MM/yyyy HH:mm");
                  } catch (e) {
                    return "-";
                  }
                })();
                return (
                  <OverflowTooltip content={dateString} className="et-cell-truncate">
                    <Typography variant="small" color="blue-gray">
                      {dateString}
                    </Typography>
                  </OverflowTooltip>
                );
              })()}
            </td>

            <td className={classes}>
              <div className="et-cell-wrap">
                <CustomerDisplay customer={quotation.customer} size="small" noToolTip />
              </div>
            </td>

            <td className={classes}>
              <OverflowTooltip content={contactLabel} className="et-cell-truncate">
                <ContactDisplay contact={quotation.contact} size="small" noToolTip />
              </OverflowTooltip>
            </td>

            <td className={classes}>
              <OverflowTooltip content={technicianLabel} className="et-cell-truncate">
                <TechnicianDisplay technician={quotation.technician} size="small" noToolTip />
              </OverflowTooltip>
            </td>

            <td className={classes}>
              <div className={isSmallScreen ? "et-cell-centered" : ""}>
                {getQuotationStatusBadge(quotation.status)}
              </div>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}
/***/
